import { useRef, useState } from 'react';
import type { MapMode } from '@warplan/shared';
import { useSandboxStore, type SandboxSlice } from '../state/sandbox-store.js';
import { buildSnapshotFromLive } from '../lib/map-data-source.js';
import { useMapObjects } from '../lib/map-objects-api.js';
import { useMapArrows } from '../lib/map-arrows-api.js';
import { useWarPlan } from '../lib/war-plan-api.js';
import { useOperationPlan } from '../lib/operation-plan-api.js';

export interface SandboxActionsMenuProps {
  /** Active sandbox flavour the actions operate on. */
  flavour: MapMode;
}

/**
 * Calculate the total size of localStorage data in bytes.
 */
function getLocalStorageSize(): number {
  let total = 0;
  for (const key in localStorage) {
    if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
      total += new Blob([localStorage[key]]).size + new Blob([key]).size;
    }
  }
  return total;
}

/**
 * Format bytes to a human-readable string.
 */
function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  }
  const kb = bytes / 1024;
  return `${kb.toFixed(1)} KB`;
}

/**
 * Compact dropdown shown in the topbar when a sandbox view is active.
 * Lets the user:
 *  - reset the current sandbox flavour (objects + arrows + plan),
 *  - clone the corresponding live state into the sandbox,
 *  - export the current sandbox slice as JSON,
 *  - import a previously exported JSON file.
 *
 * All actions are local-only — no API calls are issued.
 */
export function SandboxActionsMenu({ flavour }: SandboxActionsMenuProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmClone, setConfirmClone] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const slice = useSandboxStore((s) => (flavour === 'operation' ? s.operation : s.planner));
  const resetFlavour = useSandboxStore((s) => s.resetFlavour);
  const replaceFlavour = useSandboxStore((s) => s.replaceFlavour);

  // Live data — used only when the user actively triggers "Clone live".
  const liveObjects = useMapObjects();
  const liveArrows = useMapArrows();
  const liveWarPlan = useWarPlan();
  const liveOperationPlan = useOperationPlan();

  function onExport(): void {
    const blob = new Blob([JSON.stringify(slice, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    a.download = `sandbox-${flavour}-${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setOpen(false);
  }

  function onImportClick(): void {
    fileRef.current?.click();
  }

  function onImportFile(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const raw = reader.result;
        const text = typeof raw === 'string' ? raw : '';
        const parsed = JSON.parse(text) as SandboxSlice;
        if (!Array.isArray(parsed.objects) || !Array.isArray(parsed.arrows)) {
          throw new Error('invalid file');
        }
        replaceFlavour(flavour, parsed);
        setOpen(false);
      } catch {
        alert('Fichier sandbox invalide.');
      }
    };
    reader.readAsText(file);
    // Reset to allow re-importing the same file later.
    e.target.value = '';
  }

  function onCloneLive(): void {
    const snapshot = buildSnapshotFromLive(
      flavour,
      liveObjects.data?.objects ?? [],
      liveArrows.data?.arrows ?? [],
      liveWarPlan.data ?? null,
      liveOperationPlan.data ?? null,
    );
    replaceFlavour(flavour, snapshot);
    setConfirmClone(false);
    setOpen(false);
  }

  const objectCount = slice.objects.length;
  const arrowCount = slice.arrows.length;

  const storageSizeUsed = getLocalStorageSize();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Actions sandbox"
        aria-haspopup="menu"
        aria-expanded={open}
        className="rounded border border-amber-600 bg-amber-900/40 px-2 py-0.5 text-xs text-amber-200 hover:bg-amber-800/60"
      >
        🧪 Sandbox Options
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-1 w-72 rounded border border-slate-700 bg-slate-900 p-2 text-xs text-slate-100 shadow-2xl"
          onMouseLeave={() => setOpen(false)}
        >
          <p className="mb-2 px-1 text-[10px] uppercase tracking-wider text-amber-300">
            Sandbox {flavour} — {objectCount} obj · {arrowCount} flèches
          </p>
          <p className="mb-3 px-1 text-[10px] text-slate-400">
            Stockage du domaine : {formatBytes(storageSizeUsed)} / 5 MB
          </p>
          <button
            type="button"
            onClick={() => setConfirmClone(true)}
            className="block w-full rounded px-2 py-1 text-left hover:bg-slate-800"
            role="menuitem"
          >
            Copier l'état live vers la sandbox…
          </button>
          <button
            type="button"
            onClick={onExport}
            className="block w-full rounded px-2 py-1 text-left hover:bg-slate-800"
            role="menuitem"
          >
            Exporter en JSON
          </button>
          <button
            type="button"
            onClick={onImportClick}
            className="block w-full rounded px-2 py-1 text-left hover:bg-slate-800"
            role="menuitem"
          >
            Importer un JSON…
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={onImportFile}
          />
          <hr className="my-1 border-slate-700" />
          <button
            type="button"
            onClick={() => setConfirmReset(true)}
            className="block w-full rounded px-2 py-1 text-left text-red-300 hover:bg-red-900/40"
            role="menuitem"
          >
            Vider cette sandbox…
          </button>
        </div>
      )}
      {confirmReset && (
        <ConfirmDialog
          title="Vider la sandbox ?"
          message={`Cette action supprime ${objectCount} objet(s) et ${arrowCount} flèche(s) de la sandbox ${flavour}. Irréversible.`}
          confirmLabel="Vider"
          danger
          onCancel={() => setConfirmReset(false)}
          onConfirm={() => {
            resetFlavour(flavour);
            setConfirmReset(false);
            setOpen(false);
          }}
        />
      )}
      {confirmClone && (
        <ConfirmDialog
          title="Copier l'état live ?"
          message="Le contenu actuel de la sandbox sera remplacé par l'état synchronisé courant."
          confirmLabel="Copier"
          onCancel={() => setConfirmClone(false)}
          onConfirm={onCloneLive}
        />
      )}
    </div>
  );
}

interface ConfirmDialogProps {
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmDialog({
  title,
  message,
  confirmLabel,
  danger = false,
  onCancel,
  onConfirm,
}: ConfirmDialogProps): React.JSX.Element {
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="w-96 rounded border border-slate-700 bg-slate-900 p-5 shadow-2xl">
        <p className="mb-3 text-sm font-semibold text-slate-100">{title}</p>
        <p className="mb-5 text-xs text-slate-400">{message}</p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded bg-slate-700 px-3 py-1.5 text-xs text-slate-100 hover:bg-slate-600"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={
              'rounded px-3 py-1.5 text-xs text-white ' +
              (danger ? 'bg-red-700 hover:bg-red-600' : 'bg-emerald-700 hover:bg-emerald-600')
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
